import { useState, useMemo } from 'react';
import { Slider } from './ui/slider';
import { Droplets, TrendingUp, TrendingDown, AlertCircle, Plus, Minus, ArrowRight } from 'lucide-react';
import { fmtUsd, fmtPrice, fmtNum } from '../lib/numfmt';

const SWAP_FEE = 0.003;
const TRADE_SIZES = [100, 500, 1000, 5000, 10000, 50000, 100000, 500000, 1000000];
const PRESETS = [0.1, 0.25, 0.5, 0.75, 1, 1.5, 2, 3, 5, 10];

function getAmountOut(amountIn, reserveIn, reserveOut) {
  if (amountIn <= 0 || reserveIn <= 0 || reserveOut <= 0) return 0;
  const inWithFee = amountIn * (1 - SWAP_FEE);
  return (inWithFee * reserveOut) / (reserveIn + inWithFee);
}

function buildTable(Rt, Rb, Pb) {
  const currentPrice = (Rb * Pb) / Rt;
  const rows = TRADE_SIZES.map((size) => {
    // Buy: spend `size` USD → base in
    const baseIn = size / Pb;
    const tokensOut = getAmountOut(baseIn, Rb, Rt);
    const buyNewPrice = tokensOut > 0 && tokensOut < Rt ? ((Rb + baseIn) * Pb) / (Rt - tokensOut) : null;
    // Sell: `size` USD worth of token at current price → base out
    const tokenIn = size / currentPrice;
    const baseOut = getAmountOut(tokenIn, Rt, Rb);
    const sellNewPrice = baseOut > 0 && baseOut < Rb ? ((Rb - baseOut) * Pb) / (Rt + tokenIn) : null;
    return {
      size,
      buyImpact: buyNewPrice ? ((buyNewPrice - currentPrice) / currentPrice) * 100 : null,
      buyNewPrice,
      sellImpact: sellNewPrice ? ((sellNewPrice - currentPrice) / currentPrice) * 100 : null,
      sellNewPrice,
    };
  });
  return { currentPrice, rows };
}

function ImpactCell({ value, isBuy }) {
  if (value == null) return <td className="px-2 py-1.5 text-center text-xs text-muted-foreground">—</td>;
  const color = isBuy ? (value > 0 ? 'text-success' : 'text-destructive') : (value < 0 ? 'text-destructive' : 'text-success');
  return <td className={`px-2 py-1.5 text-center text-xs font-mono font-semibold ${color}`}>{value > 0 ? '+' : ''}{value.toFixed(2)}%</td>;
}

/**
 * Generic, pool-reserve-driven liquidity-impact simulator (any chain / pool).
 * Shows how buy/sell price impact changes if the pool's liquidity is scaled.
 */
export default function LiquidityImpactSimulator({ tokenMeta, reserves, loading, error }) {
  const [mult, setMult] = useState(1.0);
  const [mode, setMode] = useState('buy');

  const parsed = useMemo(() => {
    if (!reserves) return null;
    const RtRaw = parseFloat(reserves.token?.reserve);
    const Rb = parseFloat(reserves.base?.reserve);
    const Pb = parseFloat(reserves.base?.usd_price);
    if (!(RtRaw > 0 && Rb > 0)) return { bad: 'reserves' };
    if (!(Pb > 0)) return { bad: 'price', Rt: RtRaw, Rb };
    // Anchor token reserve to market price via base depth (see PoolSwapSimulator).
    const marketPrice = reserves.token_price_usd || (Rb * Pb) / RtRaw;
    const Rt = marketPrice > 0 ? (Rb * Pb) / marketPrice : RtRaw;
    return { Rt, Rb, Pb };
  }, [reserves]);

  const tables = useMemo(() => {
    if (!parsed || parsed.bad) return null;
    const { Rt, Rb, Pb } = parsed;
    const current = buildTable(Rt, Rb, Pb);
    const sim = buildTable(Rt * mult, Rb * mult, Pb);
    const simLiqUsd = Rb * mult * Pb * 2;
    return { current, sim, simLiqUsd };
  }, [parsed, mult]);

  if (loading) return <div className="h-64 animate-pulse bg-muted/40 rounded" data-testid="liq-sim-loading" />;
  if (error) {
    return <div className="flex items-center gap-2 text-sm text-destructive py-8 justify-center" data-testid="liq-sim-error"><AlertCircle className="w-4 h-4" /> {error}</div>;
  }
  if (!parsed || parsed.bad === 'reserves') {
    return <div className="text-sm text-muted-foreground py-8 text-center" data-testid="liq-sim-empty">Select a pool to model liquidity impact.</div>;
  }
  if (parsed.bad === 'price') {
    return (
      <div className="flex flex-col items-center gap-2 text-sm py-10 text-center" data-testid="liq-sim-no-usd">
        <AlertCircle className="w-6 h-6 text-warning" />
        <div className="font-semibold">USD pricing unavailable for this pool's base token</div>
        <p className="text-muted-foreground max-w-md text-xs">Liquidity impact needs a USD price on the quote side. Try a stable/ETH-quoted pool.</p>
      </div>
    );
  }

  const tokenSym = reserves.token?.symbol || tokenMeta?.symbol || 'TOKEN';
  const quoteSym = reserves.base?.symbol || 'QUOTE';
  const supply = parseFloat(tokenMeta?.total_supply) || 0;
  const liqChange = (mult - 1) * 100;
  const curLiqUsd = parsed.Rb * parsed.Pb * 2;

  return (
    <div className="space-y-5" data-testid="liquidity-impact-simulator">
      <div className="flex flex-wrap gap-3">
        <div className="flex border border-border overflow-hidden rounded">
          <button onClick={() => setMode('buy')} data-testid="liq-mode-buy"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${mode === 'buy' ? 'bg-success text-background' : 'bg-card text-muted-foreground hover:text-foreground'}`}>
            <TrendingUp className="w-3 h-3" /> Buy Impact
          </button>
          <button onClick={() => setMode('sell')} data-testid="liq-mode-sell"
            className={`px-3 py-1.5 text-xs font-medium flex items-center gap-1 ${mode === 'sell' ? 'bg-destructive text-destructive-foreground' : 'bg-card text-muted-foreground hover:text-foreground'}`}>
            <TrendingDown className="w-3 h-3" /> Sell Impact
          </button>
        </div>
      </div>

      {/* Current pool snapshot */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <div className="border border-border p-3"><div className="mono-label">Current {tokenSym}</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtNum(parsed.Rt)}</div></div>
        <div className="border border-border p-3"><div className="mono-label">Current {quoteSym}</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtNum(parsed.Rb)}</div></div>
        <div className="border border-border p-3"><div className="mono-label">Current liquidity</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtUsd(curLiqUsd)}</div></div>
        <div className="border border-border p-3"><div className="mono-label">Current price</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtPrice(tables?.current.currentPrice)}</div></div>
      </div>

      {/* Liquidity multiplier */}
      <div className="border border-border p-4">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm font-semibold flex items-center gap-2">
            {liqChange > 0 ? <Plus className="w-4 h-4 text-success" /> : liqChange < 0 ? <Minus className="w-4 h-4 text-destructive" /> : <ArrowRight className="w-4 h-4 text-muted-foreground" />}
            Adjust liquidity
          </h3>
          <span className={`text-sm font-mono font-bold ${mult > 1 ? 'text-success' : mult < 1 ? 'text-destructive' : 'text-muted-foreground'}`} data-testid="liq-mult-label">
            {mult.toFixed(2)}x{liqChange !== 0 && <span className="ml-1 text-xs">({liqChange > 0 ? '+' : ''}{liqChange.toFixed(0)}%)</span>}
          </span>
        </div>
        <Slider data-testid="liq-slider" min={10} max={1000} step={5} value={[mult * 100]} onValueChange={(v) => setMult(v[0] / 100)} className="w-full" />
        <div className="flex justify-between text-[10px] text-muted-foreground mt-1">
          <span>0.1x (90% removed)</span><span>1x (current)</span><span>10x (900% added)</span>
        </div>
        <div className="flex flex-wrap gap-1.5 mt-3">
          {PRESETS.map((p) => (
            <button key={p} data-testid={`liq-preset-${p}`} onClick={() => setMult(p)}
              className={`px-2 py-1 text-[10px] font-mono rounded border transition-colors ${Math.abs(mult - p) < 0.01 ? 'border-primary bg-primary/15 text-primary' : 'border-border bg-card text-muted-foreground hover:text-foreground'}`}>
              {p}x
            </button>
          ))}
        </div>
      </div>

      {/* Simulated snapshot */}
      {tables && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
          <div className="border border-border p-3"><div className="mono-label">Sim {tokenSym}</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtNum(parsed.Rt * mult)}</div></div>
          <div className="border border-border p-3"><div className="mono-label">Sim {quoteSym}</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtNum(parsed.Rb * mult)}</div></div>
          <div className="border border-border p-3"><div className="mono-label">Sim liquidity</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtUsd(tables.simLiqUsd)}</div></div>
          <div className="border border-border p-3"><div className="mono-label">Sim price</div><div className="text-sm font-mono font-semibold mt-0.5">{fmtPrice(tables.sim.currentPrice)}</div></div>
        </div>
      )}

      {/* Impact comparison table */}
      {tables && (
        <div className="overflow-x-auto border border-border">
          <table className="w-full text-left min-w-[520px]" data-testid="liq-impact-table">
            <thead className="bg-muted/40 border-b border-border">
              <tr>
                <th className="px-2 py-2 mono-label text-[10px]">Trade size</th>
                <th className="px-2 py-2 mono-label text-[10px] text-center">Current</th>
                <th className="px-2 py-2 mono-label text-[10px] text-center">Sim ({mult.toFixed(1)}x)</th>
                <th className="px-2 py-2 mono-label text-[10px] text-center">Diff</th>
                <th className="px-2 py-2 mono-label text-[10px] text-center">New price</th>
              </tr>
            </thead>
            <tbody>
              {TRADE_SIZES.map((size, i) => {
                const key = mode === 'buy' ? 'buyImpact' : 'sellImpact';
                const pk = mode === 'buy' ? 'buyNewPrice' : 'sellNewPrice';
                const cur = tables.current.rows[i]?.[key];
                const sim = tables.sim.rows[i]?.[key];
                const diff = (cur != null && sim != null) ? sim - cur : null;
                return (
                  <tr key={size} className="border-b border-border/60 hover:bg-muted/20">
                    <td className="px-2 py-1.5 text-xs font-mono text-muted-foreground">{fmtUsd(size)}</td>
                    <ImpactCell value={cur} isBuy={mode === 'buy'} />
                    <ImpactCell value={sim} isBuy={mode === 'buy'} />
                    <td className={`px-2 py-1.5 text-center text-xs font-mono ${diff == null || Math.abs(diff) < 0.01 ? 'text-muted-foreground' : (mode === 'buy' ? (diff < 0 ? 'text-success' : 'text-warning') : (diff > 0 ? 'text-success' : 'text-warning'))}`}>
                      {diff == null ? '—' : `${diff > 0 ? '+' : ''}${diff.toFixed(2)}%`}
                    </td>
                    <td className="px-2 py-1.5 text-center text-xs font-mono text-muted-foreground">{fmtPrice(tables.sim.rows[i]?.[pk])}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {/* MC impact summary */}
      {tables && supply > 0 && (
        <div className="border border-border p-4">
          <h3 className="text-sm font-semibold mb-3 flex items-center gap-2"><Droplets className="w-4 h-4 text-primary" /> Market-cap impact at simulated liquidity</h3>
          <div className="grid grid-cols-3 gap-3 text-xs">
            {[1000, 10000, 100000].map((size) => {
              const row = tables.sim.rows.find((r) => r.size === size);
              if (!row) return null;
              const impact = mode === 'buy' ? row.buyImpact : row.sellImpact;
              const newPrice = mode === 'buy' ? row.buyNewPrice : row.sellNewPrice;
              const newMc = newPrice ? newPrice * supply : null;
              return (
                <div key={size} className="border border-border p-2.5">
                  <div className="mono-label text-[10px]">{fmtUsd(size)} {mode === 'buy' ? 'buy' : 'sell'}</div>
                  <div className={`font-mono font-semibold text-sm mt-0.5 ${impact == null ? 'text-muted-foreground' : mode === 'buy' ? 'text-success' : 'text-destructive'}`}>
                    {impact != null ? `${impact > 0 ? '+' : ''}${impact.toFixed(2)}%` : '—'}
                  </div>
                  {newMc != null && <div className="text-[10px] text-muted-foreground mt-0.5">MC {fmtUsd(newMc)}</div>}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}
